import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, Outlet, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  MagnifyingGlassIcon, 
  PlusIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  AdjustmentsHorizontalIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  ArrowLeftIcon,
  PhotoIcon,
  TagIcon,
  QueueListIcon,
  ListBulletIcon,
  QrCodeIcon,
  ArrowDownOnSquareIcon,
  ArrowUpOnSquareIcon,
  XMarkIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import { LocalStorageService } from '../../utils/localStorageService';

// Importer les composants
import ProductForm from '../../components/products/ProductForm';
import ProductCategories from '../../components/products/ProductCategories';
import BarcodeScanner from '../../components/common/BarcodeScanner';

// Stock Movement Modal Component
const StockMovementModal = ({ isOpen, onClose, product }) => {
  const [movementType, setMovementType] = useState(null); // 'entry' or 'exit'
  const [quantity, setQuantity] = useState(1);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const { updateProductStock, fetchAllData, warehouses } = useAppData();
  const [loading, setLoading] = useState(false);
  
  // Reset to initial state when modal opens
  useEffect(() => {
    if (isOpen) {
      setMovementType(null);
      setQuantity(1);
      // Set default warehouse if available
      if (warehouses && warehouses.length > 0) {
        setSelectedWarehouseId(warehouses[0].id);
    }
    }
  }, [isOpen, warehouses]);

  if (!isOpen || !product) return null;

  const handleSaveMovement = async () => {
    if (!quantity || quantity <= 0) {
      toast.error('La quantité doit être supérieure à 0');
      return;
    }
    
    if (!movementType) {
      toast.error('Veuillez sélectionner un type de mouvement');
      return;
    }
    
    setLoading(true);
    
    try {
      // Create a stock movement object
      const movement = {
        id: `MOV-${new Date().getFullYear()}-${Date.now().toString().slice(-6)}`,
        date: new Date().toISOString().split('T')[0],
        type: movementType === 'entry' ? 'entrée' : 'sortie',
        productId: product.id,
        location: 'Entrepôt Principal',
        notes: `Mouvement rapide depuis la fiche produit`,
        items: [{
          id: product.id,
          name: product.name,
          quantity: parseInt(quantity),
          unitPrice: movementType === 'entry' ? (product.cost || 0) : (product.price || 0),
          total: parseInt(quantity) * (movementType === 'entry' ? (product.cost || 0) : (product.price || 0))
        }]
      };
      
      try {
        console.log('MOVEMENT: Starting stock movement for product:', product.id, product.name);
        console.log('MOVEMENT: Current stock before movement:', product.stock);
        console.log('MOVEMENT: Movement type:', movementType, 'Quantity:', quantity);
        
        // Get the warehouse name for display
        const selectedWarehouse = warehouses.find(w => w.id === selectedWarehouseId) || {};
        const warehouseName = selectedWarehouse.name || 'Entrepôt inconnu';
        
        // Create a stock movement object with all required fields
        const movementData = {
        type: movementType === 'entry' ? 'entrée' : 'sortie',
        quantity: parseInt(quantity),
          location: selectedWarehouseId, // Use the actual warehouse ID
          warehouse: selectedWarehouseId, // Use the actual warehouse ID
          warehouseName: warehouseName,   // Store the name for display
          notes: `Mouvement rapide depuis la fiche produit (${warehouseName})`,
          unitPrice: movementType === 'entry' ? (product.cost || 0) : (product.price || 0),
          items: [{
            id: product.id,
            name: product.name,
            quantity: parseInt(quantity),
            unitPrice: movementType === 'entry' ? (product.cost || 0) : (product.price || 0)
          }]
        };
        
        // Direct manual update - most reliable approach
        try {
          // Get current products from both storage keys
          const productsData = localStorage.getItem('products');
          let products = [];
          
          if (productsData) {
            products = JSON.parse(productsData);
          }
          
          console.log('MOVEMENT: Found products in localStorage:', products.length);
          
          const productIndex = products.findIndex(p => p.id === product.id);
          console.log('MOVEMENT: Product index in array:', productIndex);
          
          if (productIndex !== -1) {
            // Calculate new stock
            const currentStock = parseInt(products[productIndex].stock) || 0;
            let newStock = currentStock;
            
            if (movementType === 'entry') {
              newStock = currentStock + parseInt(quantity);
            } else {
              newStock = Math.max(0, currentStock - parseInt(quantity));
            }
            
            console.log('MOVEMENT: Updating stock from', currentStock, 'to', newStock);
            
            // Update product
            products[productIndex].stock = newStock;
            products[productIndex].lastUpdated = new Date().toISOString().split('T')[0];
            
            // Update warehouse tracking with consistent ID handling
            if (!products[productIndex].warehouseIds) {
              products[productIndex].warehouseIds = [];
            }
            
            // Add warehouse to warehouseIds if not already there (ensuring string comparison)
            const warehouseId = movementData.warehouse;
            const warehouseIdStr = String(warehouseId);
            
            // Check if warehouseId exists using string comparison
            const warehouseIdExists = products[productIndex].warehouseIds.some(
              wId => String(wId) === warehouseIdStr
            );
            
            if (!warehouseIdExists) {
              products[productIndex].warehouseIds.push(warehouseId);
              console.log('MOVEMENT: Added warehouse', warehouseId, 'to product warehouseIds');
            }
            
            // Update warehouse-specific stock
            if (!products[productIndex].warehouseStock) {
              products[productIndex].warehouseStock = {};
            }
            
            // Calculate warehouse-specific stock (using string keys for consistency)
            const currentWarehouseStock = parseInt(products[productIndex].warehouseStock[warehouseIdStr] || 0);
            let newWarehouseStock = currentWarehouseStock;
            
            if (movementType === 'entry') {
              newWarehouseStock = currentWarehouseStock + parseInt(quantity);
            } else {
              newWarehouseStock = Math.max(0, currentWarehouseStock - parseInt(quantity));
            }
            
            // Store with string key for consistency
            products[productIndex].warehouseStock[warehouseIdStr] = newWarehouseStock;
            console.log('MOVEMENT: Updated warehouse stock for', warehouseIdStr, 'from', currentWarehouseStock, 'to', newWarehouseStock);
            
            // If stock is 0 and this is a 'sortie' operation, consider removing from warehouseIds
            if (newWarehouseStock === 0 && movementType === 'exit') {
              console.log('MOVEMENT: Product stock is now 0 in this warehouse, but keeping in warehouseIds for history');            
            }
            
            // CRITICAL: Also update the current product object to show changes immediately
            product.stock = newStock;
            product.lastUpdated = new Date().toISOString().split('T')[0];
            product.warehouseIds = [...products[productIndex].warehouseIds];
            product.warehouseStock = {...products[productIndex].warehouseStock};
            
            // Save back to BOTH localStorage keys
            localStorage.setItem('products', JSON.stringify(products));
            localStorage.setItem('stockpro_products', JSON.stringify(products));
            
            console.log('MOVEMENT: Stock updated successfully in localStorage and in current product');
          } else {
            console.error('MOVEMENT: Could not find product with ID', product.id);
            throw new Error('Product not found in localStorage');
          }
        } catch (manualError) {
          console.error('MOVEMENT: Manual stock update failed:', manualError);
          throw manualError;
        }
        // Create the movement record without updating stock again
        // We're only recording the movement here, not updating stock
        // This prevents double-counting
        try {
          // Add the movement to stockItems without updating product stock
          const stockItemsData = localStorage.getItem('stockpro_stock');
          let stockItems = [];
          
          if (stockItemsData) {
            stockItems = JSON.parse(stockItemsData);
          }
          
          // Create a new movement record with consistent warehouse ID handling
          const movementRecord = {
            id: `mvt-${Date.now()}`,
            date: new Date().toISOString().split('T')[0],
            type: movementData.type,
            productId: product.id,
            quantity: parseInt(movementData.quantity),
            location: selectedWarehouseId, // Use the actual warehouse ID
            warehouse: selectedWarehouseId, // Use the actual warehouse ID
            warehouseName: movementData.warehouseName, // Store the name for display
            // For transfers, ensure sourceWarehouse and destinationWarehouse are set
            sourceWarehouse: selectedWarehouseId,
            destinationWarehouse: selectedWarehouseId,
            notes: movementData.notes,
            unitPrice: movementData.unitPrice || 0,
            items: movementData.items || [],
            // Add metadata to help with debugging
            meta: {
              createdAt: new Date().toISOString(),
              source: 'StockMovementModal',
              productName: product.name,
              warehouseIdType: typeof selectedWarehouseId
            }
          };
          
          // Add to stockItems
          stockItems.push(movementRecord);
          
          // Save to localStorage
          localStorage.setItem('stockpro_stock', JSON.stringify(stockItems));
          
          console.log('Movement record created successfully without updating stock again');
        } catch (movementError) {
          console.error('Error creating movement record:', movementError);
        }
        
        // Add a short delay to ensure everything is saved before showing success message
        await new Promise(resolve => setTimeout(resolve, 100));
        toast.success(`${movementType === 'entry' ? 'Entrée' : 'Sortie'} de stock enregistrée avec succès`);
        
        // Immediately dispatch the event to update the UI without waiting
        const event = new CustomEvent('product-stock-updated', { 
          detail: { 
            productId: product.id,
            productName: product.name,
            newStock: product.stock,  // We updated this in line 135
            warehouseId: selectedWarehouseId,
            warehouseIdStr: String(selectedWarehouseId), // Ensure string version is available
            warehouseName: movementData.warehouseName,
            movementType: movementType === 'entry' ? 'entrée' : 'sortie',
            quantity: parseInt(quantity),
            warehouseStock: product.warehouseStock ? {...product.warehouseStock} : {},
            warehouseIds: product.warehouseIds ? [...product.warehouseIds] : [],
            timestamp: new Date().toISOString(),
            movementId: `mvt-${Date.now()}`
          } 
        });
        console.log('MOVEMENT: Dispatching detailed product-stock-updated event', event.detail);
        window.dispatchEvent(event);
        
        // Notify about successful update
        toast.success('Stock mis à jour avec succès!');
        
        // Don't call fetchAllData() as it might overwrite our changes with stale API data
      } catch (error) {
        console.error('MOVEMENT: Error updating stock:', error);
        toast.error('Erreur lors de la mise à jour du stock');
      }
      
      toast.success(`${movementType === 'entry' ? 'Entrée' : 'Sortie'} de stock enregistrée avec succès`);
      setLoading(false);
      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du mouvement de stock:', error);
      toast.error('Erreur lors de l\'enregistrement du mouvement de stock');
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div 
          className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 w-full max-w-lg"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white">Ajouter un mouvement</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          
          {movementType === null ? (
            <div className="flex flex-col space-y-4">
              <p className="text-white text-center mb-4">Choisissez le type de mouvement pour {product?.name}</p>
              
              <button
                onClick={() => setMovementType('entry')}
                className="flex items-center justify-center space-x-3 bg-green-500/20 text-green-400 py-6 px-4 rounded-xl hover:bg-green-500/30 transition-colors"
              >
                <ArrowDownOnSquareIcon className="h-8 w-8" />
                <div className="text-left">
                  <h3 className="font-medium text-lg">Entrée de stock</h3>
                  <p className="text-green-400/80">Ajouter des unités au stock</p>
                </div>
              </button>
              
              <button
                onClick={() => setMovementType('exit')}
                className="flex items-center justify-center space-x-3 bg-red-500/20 text-red-400 py-6 px-4 rounded-xl hover:bg-red-500/30 transition-colors"
              >
                <ArrowUpOnSquareIcon className="h-8 w-8" />
                <div className="text-left">
                  <h3 className="font-medium text-lg">Sortie de stock</h3>
                  <p className="text-red-400/80">Retirer des unités du stock</p>
                </div>
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-white/5 p-4 rounded-lg">
                <p className="text-white/70 text-sm">Produit</p>
                <p className="text-white font-medium">{product?.name}</p>
                <p className="text-white/70 text-sm mt-2">Stock actuel</p>
                <p className="text-white font-medium">{product?.stock || 0} {product?.unit || 'unités'}</p>
              </div>
              
              <div>
                <label className="block text-white mb-2">Entrepôt</label>
                <select
                  value={selectedWarehouseId}
                  onChange={(e) => setSelectedWarehouseId(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {warehouses && warehouses.length > 0 ? (
                    warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))
                  ) : (
                    <option value="">Aucun entrepôt disponible</option>
                  )}
                </select>
              </div>
              
              <div>
                <label className="block text-white mb-2">Quantité</label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 0))}
                  min="1"
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {movementType === 'exit' && product && (product.stock || 0) < quantity && (
                  <p className="text-red-400 text-sm mt-1">
                    Attention: La quantité dépasse le stock disponible ({product.stock || 0})
                  </p>
                )}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setMovementType(null)}
                  className="px-4 py-2 border border-white/20 text-white rounded-lg hover:bg-white/10"
                >
                  Retour
                </button>
                
                <button
                  onClick={handleSaveMovement}
                  disabled={loading || (movementType === 'exit' && product && (product.stock || 0) < quantity)}
                  className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                    movementType === 'entry' 
                      ? 'bg-gradient-to-r from-green-500 to-green-600 text-white' 
                      : 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                  } ${loading || (movementType === 'exit' && product && (product.stock || 0) < quantity) ? 'opacity-50 cursor-not-allowed' : 'hover:from-green-600 hover:to-green-700'}`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Traitement...</span>
                    </>
                  ) : (
                    <>
                      {movementType === 'entry' ? (
                        <>
                          <ArrowDownOnSquareIcon className="h-5 w-5" />
                          <span>Valider l'entrée</span>
                        </>
                      ) : (
                        <>
                          <ArrowUpOnSquareIcon className="h-5 w-5" />
                          <span>Valider la sortie</span>
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Page principale des produits avec onglets
const ProductsMainPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('catalogue');
  const [refreshKey, setRefreshKey] = useState(0); // Add this state to force refresh
  const addCategoryRef = useRef(null);
  const { products, setProducts, productCategories, fetchAllData } = useAppData();

  // Déterminer l'onglet actif basé sur l'URL
  useEffect(() => {
    if (location.pathname.includes('/products/categories')) {
      setActiveTab('categories');
    } else if (location.pathname.includes('/products/barcode')) {
      setActiveTab('barcode');
    } else {
      setActiveTab('catalogue');
    }
  }, [location.pathname]);
  
  // Listen for stock updates from the StockMovementModal
  useEffect(() => {
    const handleStockUpdate = (event) => {
      console.log('Stock update event received for product:', event.detail.productId);
      
      // Update the specific product in the local state without a full reload
      if (event.detail.productId && event.detail.newStock !== undefined) {
        try {
          // Step 1: Update localStorage directly (both keys)
          try {
            // Get current products from both localStorage keys
            const productsData = localStorage.getItem('products');
            const stockproProductsData = localStorage.getItem('stockpro_products');
            
            if (productsData) {
              const localProducts = JSON.parse(productsData);
              const productIndex = localProducts.findIndex(p => p.id === event.detail.productId);
              
              if (productIndex !== -1) {
                console.log(`Updating localStorage 'products' for ${event.detail.productId} from ${localProducts[productIndex].stock} to ${event.detail.newStock}`);
                localProducts[productIndex].stock = event.detail.newStock;
                localProducts[productIndex].lastUpdated = new Date().toISOString().split('T')[0];
                localStorage.setItem('products', JSON.stringify(localProducts));
              }
            }
            
            if (stockproProductsData) {
              const stockproProducts = JSON.parse(stockproProductsData);
              const productIndex = stockproProducts.findIndex(p => p.id === event.detail.productId);
              
              if (productIndex !== -1) {
                console.log(`Updating localStorage 'stockpro_products' for ${event.detail.productId} from ${stockproProducts[productIndex].stock} to ${event.detail.newStock}`);
                stockproProducts[productIndex].stock = event.detail.newStock;
                stockproProducts[productIndex].lastUpdated = new Date().toISOString().split('T')[0];
                localStorage.setItem('stockpro_products', JSON.stringify(stockproProducts));
              }
            }
            
            console.log('Product stock updated in localStorage successfully');
          } catch (error) {
            console.error('Error updating localStorage:', error);
          }
          
          // Step 2: Update the products array in component state directly
          // This is the key change - we're updating our local state directly instead of using context
          const updatedProducts = products.map(p => {
            if (p.id === event.detail.productId) {
              console.log(`Directly updating local state for product ${p.id} from ${p.stock} to ${event.detail.newStock}`);
              return {
                ...p,
                stock: event.detail.newStock,
                lastUpdated: new Date().toISOString().split('T')[0]
              };
            }
            return p;
          });
          
          // Force component re-render with updated products
          setProducts(updatedProducts);
          
          // Force re-render of the component with refreshKey
          setRefreshKey(prevKey => prevKey + 1);
          
        } catch (error) {
          console.error('Error in handleStockUpdate:', error);
        }
      }
    };
    
    window.addEventListener('product-stock-updated', handleStockUpdate);
    
    return () => {
      window.removeEventListener('product-stock-updated', handleStockUpdate);
    };
  }, [products]);

  // Gérer le changement d'onglet
  const handleTabChange = (tab) => {
    if (tab === 'catalogue') {
      navigate('/products');
    } else if (tab === 'categories') {
      navigate('/products/categories');
    } else if (tab === 'barcode') {
      navigate('/products/barcode');
    }
    setActiveTab(tab);
  };

  const handleAction = () => {
    if (activeTab === 'catalogue') {
      navigate('/products/new');
    } else if (activeTab === 'categories') {
      // Pour l'onglet catégories, nous appelons la fonction d'ajout de catégorie
      if (addCategoryRef.current) {
        addCategoryRef.current();
      }
    } else if (activeTab === 'barcode') {
      // Pour l'onglet code-barres, ouvrir directement le scanner
      navigate('/products/barcode/scan');
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête de la page */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Produits</h1>
          <p className="text-white/60 mt-2">Gérez votre catalogue de produits</p>
        </div>
        <button 
          onClick={handleAction}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>
            {activeTab === 'catalogue' ? 'Nouveau produit' : 
             activeTab === 'categories' ? 'Ajouter catégorie' : 
             'Scanner code-barres'}
          </span>
        </button>
      </div>

      {/* Onglets */}
      <div className="flex space-x-3 bg-white/10 backdrop-blur-lg rounded-2xl p-1">
        <button 
          onClick={() => handleTabChange('catalogue')}
          className={`flex-1 py-3 rounded-xl flex justify-center items-center space-x-2 transition-colors ${
            activeTab === 'catalogue' 
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' 
              : 'text-white/70 hover:text-white/90'
          }`}
        >
          <ListBulletIcon className="h-5 w-5" />
          <span className="font-medium">Catalogue</span>
        </button>
        <button 
          onClick={() => handleTabChange('categories')}
          className={`flex-1 py-3 rounded-xl flex justify-center items-center space-x-2 transition-colors ${
            activeTab === 'categories' 
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' 
              : 'text-white/70 hover:text-white/90'
          }`}
        >
          <TagIcon className="h-5 w-5" />
          <span className="font-medium">Catégories</span>
        </button>
        <button 
          onClick={() => handleTabChange('barcode')}
          className={`flex-1 py-3 rounded-xl flex justify-center items-center space-x-2 transition-colors ${
            activeTab === 'barcode' 
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' 
              : 'text-white/70 hover:text-white/90'
          }`}
        >
          <QrCodeIcon className="h-5 w-5" />
          <span className="font-medium">Code-barres</span>
        </button>
      </div>

      {/* Contenu de l'onglet - utiliser les conditions pour afficher le bon contenu */}
      {activeTab === 'catalogue' && <ProductsList />}
      {activeTab === 'categories' && <ProductCategories ref={addCategoryRef} />}
      {activeTab === 'barcode' && <BarcodeManager />}
    </div>
  );
};

// Cache for Object URLs to prevent memory leaks
const objectUrlCache = new Map();

// Liste des produits (maintenant un sous-composant)
const ProductsList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStock, setSelectedStock] = useState('all');
  const [selectedPrice, setSelectedPrice] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Utiliser le contexte AppData pour les produits
  const { 
    products, 
    addProduct, 
    updateProduct, 
    deleteProduct,
    globalStats,
    productCategories,
    fetchAllData
  } = useAppData();

  // Load products from context on mount - context will fetch from API
  useEffect(() => {
    console.log('ProductsList component mounted/visited, loading via context...');
    setLoading(true);
    
    // Fetch fresh data from API via context
    fetchAllData().then(() => {
      setLoading(false);
    }).catch((error) => {
      console.error('Error loading products via context:', error);
      setLoading(false);
    });
  }, [fetchAllData]);

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      objectUrlCache.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      objectUrlCache.clear();
    };
  }, []);
  
  // Function to safely get image URL - now images are stored as base64 strings
  const getImageUrl = (image) => {
    // If it's already a string (base64 or URL), return it directly
    if (typeof image === 'string') {
      return image;
    } 
    
    // For backward compatibility with File/Blob objects
    if (image instanceof Blob || image instanceof File) {
      // Check if we already have an object URL for this image
      const imageId = image.lastModified ? `${image.name}-${image.lastModified}` : image.size.toString();
      
      if (!objectUrlCache.has(imageId)) {
        const url = URL.createObjectURL(image);
        objectUrlCache.set(imageId, url);
      }
      
      return objectUrlCache.get(imageId);
    }
    
    return '';
  };

  // Liste des catégories unique extraites des produits
  const categories = productCategories && productCategories.length > 0 
    ? productCategories 
    : [...new Set(products.map(product => product.category))].filter(Boolean);

  // Filtrer les produits
  const filteredProducts = products.filter(product => {
    // Filtre de recherche textuelle
    const matchesSearch = searchTerm === '' || 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase())) ||
      product.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtre de catégorie
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    
    // Filtre de stock
    let matchesStock = true;
    const stock = parseInt(product.stock) || 0;
    const minStock = parseInt(product.minStockLevel || product.minStock) || 0;
    
    if (selectedStock === 'in-stock') {
      matchesStock = stock > 0;
    } else if (selectedStock === 'low-stock') {
      matchesStock = stock > 0 && stock <= minStock;
    } else if (selectedStock === 'out-of-stock') {
      matchesStock = stock === 0;
    }
    
    // Filtre de prix
    let matchesPrice = true;
    const price = parseInt(product.price) || 0;
    
    if (selectedPrice === '0-1000') {
      matchesPrice = price >= 0 && price <= 1000;
    } else if (selectedPrice === '1000-5000') {
      matchesPrice = price > 1000 && price <= 5000;
    } else if (selectedPrice === '5000+') {
      matchesPrice = price > 5000;
    }
    
    return matchesSearch && matchesCategory && matchesStock && matchesPrice;
  });

  // Stats sur les produits
  const productStats = [
    {
      title: 'Total des produits',
      value: products.length,
      icon: PlusIcon,
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Valeur du stock',
      value: `${products.reduce((sum, product) => sum + ((product.price || 0) * (product.stock || 0)), 0).toLocaleString('fr-DZ')} DA`,
      icon: ArrowUpTrayIcon,
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'Catégories',
      value: categories.length,
      icon: AdjustmentsHorizontalIcon,
      color: 'from-purple-500 to-purple-600',
    }
  ];

  // Gérer la visualisation d'un produit
  const handleViewProduct = (id) => {
    navigate(`/products/${id}`);
  };

  // Gérer la modification d'un produit
  const handleEditProduct = (id) => {
    navigate(`/products/${id}/edit`);
  };

  // Gérer la suppression d'un produit
  const handleDeleteProduct = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      // Utiliser la fonction du contexte pour supprimer
      deleteProduct(id);
      toast.success('Produit supprimé avec succès');
    }
  };
  
  // Function to handle opening the stock movement modal
  const handleStockMovement = (product) => {
    setSelectedProduct(product);
    setShowStockModal(true);
  };

  // Gérer l'exportation des produits
  const handleExportProducts = () => {
    // Dans une vraie application, cela génèrerait un fichier CSV/Excel
    toast.success('Export des produits démarré');
  };

  // Gérer l'importation des produits
  const handleImportProducts = () => {
    // Dans une vraie application, cela ouvrirait un modal pour télécharger un fichier
    toast.success('La fonctionnalité d\'import sera bientôt disponible');
  };
  
  // Show loading spinner with a maximum timeout
  useEffect(() => {
    if (loading) {
      // Safety timeout to prevent infinite loading
      const timer = setTimeout(() => {
        setLoading(false);
        console.log("Loading timeout reached, forcing display of products");
      }, 1500); // Max 1.5 seconds of loading
      
      return () => clearTimeout(timer);
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stock Movement Modal */}
      <StockMovementModal 
        isOpen={showStockModal} 
        onClose={() => {
          setShowStockModal(false);
          fetchAllData(); // Refresh data after movement
        }} 
        product={selectedProduct}
      />
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {productStats.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Barre de recherche et filtres */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-grow">
            <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Rechercher un produit par nom, code, ou référence..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <button 
              onClick={() => setFilterOpen(!filterOpen)}
              className={`px-4 py-2.5 rounded-xl flex items-center space-x-2 transition-colors ${
                filterOpen ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' : 
                selectedCategory !== 'all' || selectedStock !== 'all' || selectedPrice !== 'all' ? 
                'bg-gradient-to-r from-blue-600/60 to-purple-600/60 text-white' : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <AdjustmentsHorizontalIcon className="h-5 w-5" />
              <span>Filtres</span>
              {(selectedCategory !== 'all' || selectedStock !== 'all' || selectedPrice !== 'all') && (
                <span className="flex items-center justify-center bg-white text-purple-600 rounded-full h-5 w-5 text-xs font-medium">
                  {(selectedCategory !== 'all' ? 1 : 0) + 
                   (selectedStock !== 'all' ? 1 : 0) + 
                   (selectedPrice !== 'all' ? 1 : 0)}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filtres */}
        {filterOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-6 pt-6 border-t border-white/10"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-white/70 mb-2">
                  Catégorie
                </label>
                <select
                  id="category"
                  className="block w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="all">Toutes les catégories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="stock" className="block text-sm font-medium text-white/70 mb-2">
                  Stock
                </label>
                <select
                  id="stock"
                  className="block w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedStock}
                  onChange={(e) => setSelectedStock(e.target.value)}
                >
                  <option value="all">Tous</option>
                  <option value="in-stock">En stock</option>
                  <option value="low-stock">Stock faible</option>
                  <option value="out-of-stock">Rupture de stock</option>
                </select>
              </div>
              <div>
                <label htmlFor="price" className="block text-sm font-medium text-white/70 mb-2">
                  Prix
                </label>
                <select
                  id="price"
                  className="block w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedPrice}
                  onChange={(e) => setSelectedPrice(e.target.value)}
                >
                  <option value="all">Tous</option>
                  <option value="0-1000">0 - 1 000 DA</option>
                  <option value="1000-5000">1 000 - 5 000 DA</option>
                  <option value="5000+">5 000+ DA</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-4">
              <button 
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedStock('all');
                  setSelectedPrice('all');
                  toast.success('Filtres réinitialisés');
                }}
                className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors font-medium"
              >
                Réinitialiser
              </button>
              <button 
                onClick={() => {
                  toast.success('Filtres appliqués');
                  setFilterOpen(false);
                }}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl transition-colors hover:from-blue-700 hover:to-purple-700 font-medium"
              >
                Appliquer les filtres
              </button>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Liste des produits */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden"
      >
        {/* Résumé des filtres et résultats */}
        {(selectedCategory !== 'all' || selectedStock !== 'all' || selectedPrice !== 'all') && (
          <div className="px-6 py-3 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-b border-white/10 flex justify-between items-center">
            <div className="text-white">
              <span className="font-medium">{filteredProducts.length}</span> produit(s) correspondent à vos filtres
              {selectedCategory !== 'all' && <span className="ml-2 px-2 py-0.5 bg-blue-500/20 rounded-full text-xs">{selectedCategory}</span>}
              {selectedStock !== 'all' && (
                <span className="ml-2 px-2 py-0.5 bg-green-500/20 rounded-full text-xs">
                  {selectedStock === 'in-stock' ? 'En stock' : 
                   selectedStock === 'low-stock' ? 'Stock faible' : 'Rupture'}
                </span>
              )}
              {selectedPrice !== 'all' && (
                <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 rounded-full text-xs">
                  {selectedPrice === '0-1000' ? '0-1000 DA' : 
                   selectedPrice === '1000-5000' ? '1000-5000 DA' : '5000+ DA'}
                </span>
              )}
            </div>
            <button
              onClick={() => {
                setSelectedCategory('all');
                setSelectedStock('all');
                setSelectedPrice('all');
                toast.success('Filtres réinitialisés');
              }}
              className="text-white/70 hover:text-white text-sm flex items-center space-x-1"
            >
              <span>Réinitialiser</span>
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th scope="col" className="px-6 py-4 text-left text-white/70 font-medium">
                  Produit
                </th>
                <th scope="col" className="px-6 py-4 text-left text-white/70 font-medium">
                  Catégorie
                </th>
                <th scope="col" className="px-6 py-4 text-left text-white/70 font-medium">
                  ID
                </th>
                <th scope="col" className="px-6 py-4 text-right text-white/70 font-medium">
                  Prix
                </th>
                <th scope="col" className="px-6 py-4 text-right text-white/70 font-medium">
                  Coût
                </th>
                <th scope="col" className="px-6 py-4 text-right text-white/70 font-medium">
                  Stock
                </th>
                <th scope="col" className="px-6 py-4 text-right text-white/70 font-medium">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filteredProducts.map((product) => (
                <motion.tr 
                  key={product.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-white/5 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {product.image ? (
                        <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-white/10 overflow-hidden">
                          <img 
                            src={product.image} 
                            alt={product.name} 
                            className="h-full w-full object-cover"
                          />
                        </div>
                      ) : (
                      <div className="h-10 w-10 flex-shrink-0 rounded-xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 flex items-center justify-center text-white">
                        {product.name.charAt(0)}
                      </div>
                      )}
                      <div className="ml-4">
                        <div className="text-sm font-medium text-white">{product.name}</div>
                        <div className="text-sm text-white/50 font-mono">{product.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400">
                      {product.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-white">
                    {product.sku}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white text-right font-medium">
                    {product.price ? product.price.toLocaleString('fr-DZ') : '0'} DA
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/80 text-right">
                    {product.cost ? product.cost.toLocaleString('fr-DZ') : '0'} DA
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="text-sm font-medium text-white">{product.stock} {product.unit}</div>
                    <div className="text-xs text-white/50">Mis à jour le {product.lastUpdated}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button 
                        onClick={() => handleViewProduct(product.id)} 
                        className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors" 
                        title="Voir"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleEditProduct(product.id)} 
                        className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors" 
                        title="Modifier"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleDeleteProduct(product.id)} 
                        className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors" 
                        title="Supprimer"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredProducts.length === 0 && (
          <div className="text-center py-12 text-white/50">
            <p>Aucun produit ne correspond aux critères de recherche.</p>
          </div>
        )}
        
        {/* Pagination */}
        <div className="bg-white/5 px-6 py-4 flex items-center justify-between border-t border-white/10">
          <div className="text-sm text-white/60">
            Affichage de <span className="font-medium">1</span> à <span className="font-medium">{filteredProducts.length}</span> sur <span className="font-medium">{products.length}</span> produits
          </div>
          <div className="flex space-x-2">
            <button className="px-3 py-1 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors">
              Précédent
            </button>
            <button className="px-3 py-1 bg-white/10 rounded-lg text-white hover:bg-white/20 transition-colors">
              Suivant
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Vue détaillée d'un produit (inchangée)
const ProductDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [showStockModal, setShowStockModal] = useState(false);
  const { products, fetchAllData } = useAppData();
  
  useEffect(() => {
    setLoading(true);
    
    // Find the product by ID from our actual products list
    const productData = products.find(p => p.id === id);
    
    if (productData) {
      setProduct(productData);
      setLoading(false);
    } else {
      // If product not found, try fetching from API or wait a moment
      // (for demo purposes, add a small delay to simulate network request)
    setTimeout(() => {
        // Second attempt to find product (could be an API call in real app)
        const retryProductData = products.find(p => p.id === id);
        if (retryProductData) {
          setProduct(retryProductData);
        }
      setLoading(false);
    }, 500);
    }
  }, [id, products]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-bold text-white">Produit non trouvé</h2>
        <button 
          onClick={() => navigate('/products')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
        >
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/products')}
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">{product.name}</h1>
            <p className="text-white/60 mt-1">{product.sku}</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button 
            onClick={() => navigate(`/products/${product.id}/edit`)}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center space-x-2"
          >
            <PencilSquareIcon className="h-5 w-5" />
            <span>Modifier</span>
          </button>
          <button 
            onClick={() => {
              if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
                toast.success('Produit supprimé avec succès');
                navigate('/products');
              }
            }}
            className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl flex items-center space-x-2"
          >
            <TrashIcon className="h-5 w-5" />
            <span>Supprimer</span>
          </button>
        </div>
      </div>

      {/* Contenu du produit */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne principale - Informations du produit */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informations de base */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-6">Informations générales</h2>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-white/60 text-sm">Catégorie</p>
                <p className="text-white mt-1 font-medium">{product.category}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Fournisseur</p>
                <p className="text-white mt-1 font-medium">{product.supplier}</p>
              </div>
              <div className="col-span-2">
                <p className="text-white/60 text-sm">Description</p>
                <p className="text-white mt-1">{product.description || 'Aucune description'}</p>
              </div>
            </div>
          </div>
          
          {/* Informations de prix et de stock */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-6">Prix et stock</h2>
            
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-white/60 text-sm">Prix de vente</p>
                <p className="text-white mt-1 font-medium">{product.price ? product.price.toLocaleString('fr-DZ') : '0'} DA</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Prix d'achat</p>
                <p className="text-white mt-1 font-medium">{product.cost ? product.cost.toLocaleString('fr-DZ') : '0'} DA</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Marge</p>
                <p className="text-white mt-1 font-medium">
                  {(product.price && product.cost) ? ((product.price - product.cost) / product.price * 100).toFixed(2) : '0'}%
                </p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Quantité en stock</p>
                <p className="text-white mt-1 font-medium">{product.stock} {product.unit}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Stock minimum</p>
                <p className="text-white mt-1 font-medium">{product.minStock} {product.unit}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Stock maximum</p>
                <p className="text-white mt-1 font-medium">{product.maxStock} {product.unit}</p>
              </div>
            </div>
          </div>
          
          {/* Historique des mouvements */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-6">Historique des mouvements</h2>
            
            <div className="text-center py-8">
              <p className="text-white/60">Aucun mouvement de stock pour ce produit.</p>
              <button 
                onClick={() => setShowStockModal(true)}
                className="mt-4 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors"
              >
                Ajouter un mouvement
              </button>
            </div>
          </div>
        </div>
        
        {/* Colonne de droite - Image et codes */}
        <div className="space-y-6">
          {/* Image du produit */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-4">Image du produit</h2>
            
            <div className="aspect-square rounded-xl overflow-hidden bg-white/5 flex items-center justify-center">
              {product.image ? (
                <img 
                  src={product.image} 
                  alt={product.name} 
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-white/50 flex flex-col items-center justify-center p-4 text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p>Aucune image disponible</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Codes et références */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-6">Codes et références</h2>
            
            <div className="space-y-4">
              <div>
                <p className="text-white/60 text-sm">Référence ID</p>
                <p className="text-white mt-1 font-medium font-mono">{product.id}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Code-barres</p>
                <p className="text-white mt-1 font-medium font-mono">{product.barcode || 'Non défini'}</p>
              </div>
              <div>
                <p className="text-white/60 text-sm">Identifiant</p>
                <p className="text-white mt-1 font-medium font-mono">{product.sku}</p>
              </div>
              </div>
            </div>
          </div>
        </div>
      
      {/* Modal for stock movements */}
      <StockMovementModal 
        isOpen={showStockModal} 
        onClose={() => {
          setShowStockModal(false);
          // Refresh data after closing modal
          fetchAllData().then(data => {
            // Update product data with the refreshed one
            const updatedProduct = data.products.find(p => p.id === id);
            if (updatedProduct) {
              setProduct(updatedProduct);
            }
          });
        }}
        product={product} 
      />
    </div>
  );
};

// Page des catégories de produits
const ProductCategoriesPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/products')}
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">Catégories de produits</h1>
            <p className="text-white/60 mt-1">Gérez et organisez vos catégories de produits</p>
          </div>
        </div>
      </div>

      <ProductCategories />
    </div>
  );
};

// Composant pour la gestion des codes-barres
const BarcodeManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [recentlyScanned, setRecentlyScanned] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (location.pathname.includes('/products/barcode/scan')) {
      setIsScannerOpen(true);
    } else {
      setIsScannerOpen(false);
    }
  }, [location.pathname]);

  const openScanner = () => {
    navigate('/products/barcode/scan');
  };

  const closeScanner = () => {
    navigate('/products/barcode');
  };

  // Simulation de recherche de produit par code-barres
  const searchProductByBarcode = (code) => {
    setIsLoading(true);
    setSearchQuery(code);
    
    // Simuler un appel API
    setTimeout(() => {
      const products = [
        { id: 'prod-001', name: 'T-shirt Sport', barcode: '3800012345678', price: '2500 DA', stock: 15, category: 'Vêtements', thumbnail: 'https://via.placeholder.com/50' },
        { id: 'prod-002', name: 'Pantalon Training', barcode: '3800012345685', price: '4000 DA', stock: 8, category: 'Vêtements', thumbnail: 'https://via.placeholder.com/50' },
        { id: 'prod-003', name: 'Chaussures Running', barcode: '3800012345692', price: '6000 DA', stock: 12, category: 'Chaussures', thumbnail: 'https://via.placeholder.com/50' },
      ];
      
      // Recherche du produit par code-barres
      const results = products.filter(product => product.barcode === code);
      
      if (results.length > 0) {
        setSearchResults(results);
        
        // Ajouter à la liste des récemment scannés s'il n'y est pas déjà
        setRecentlyScanned(prev => {
          // Vérifier si le produit existe déjà dans la liste
          const exists = prev.some(p => p.barcode === code);
          if (!exists) {
            // Limiter la liste à 5 éléments
            const newList = [results[0], ...prev].slice(0, 5);
            return newList;
          }
          return prev;
        });
        
        toast.success(`Produit trouvé: ${results[0].name}`);
      } else {
        setSearchResults([]);
        toast.error('Aucun produit trouvé avec ce code-barres');
      }
      
      setIsLoading(false);
    }, 800);
  };

  const handleScan = (code) => {
    searchProductByBarcode(code);
  };

  const handleScanError = (error) => {
    toast.error(`Erreur: ${error}`);
  };

  const handleViewProduct = (id) => {
    navigate(`/products/${id}`);
  };

  const handleEditProduct = (id) => {
    navigate(`/products/${id}/edit`);
  };

  const handleManualSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      searchProductByBarcode(searchQuery.trim());
    }
  };

  const handleCreateProductWithBarcode = (code) => {
    navigate(`/products/new?barcode=${code}`);
  };

  return (
    <>
      {/* Interface de recherche par code-barres */}
      <div className="space-y-6">
        {/* Zone de recherche */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
          <h2 className="text-xl font-semibold text-white mb-4">Recherche par code-barres</h2>
          
          <form onSubmit={handleManualSearch} className="flex space-x-4 mb-6">
            <div className="relative flex-grow">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Entrez un code-barres..."
                className="w-full p-3 pl-10 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
            </div>
            
            <button 
              type="submit"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center space-x-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
              ) : (
                <MagnifyingGlassIcon className="h-5 w-5" />
              )}
              <span>Rechercher</span>
            </button>
            
            <button 
              type="button"
              onClick={openScanner}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl flex items-center space-x-2"
            >
              <QrCodeIcon className="h-5 w-5" />
              <span>Scanner</span>
            </button>
          </form>

          {/* Résultats de recherche */}
          {searchResults.length > 0 ? (
            <div className="bg-white/5 rounded-2xl overflow-hidden border border-white/10">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Produit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Code-barres</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Prix</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Stock</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white/5 divide-y divide-white/10">
                  {searchResults.map((product) => (
                    <tr key={product.id} className="hover:bg-white/10 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {product.thumbnail && (
                            <div className="flex-shrink-0 h-10 w-10 rounded overflow-hidden mr-3">
                              <img src={product.thumbnail} alt={product.name} className="h-full w-full object-cover" />
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-white">{product.name}</div>
                            <div className="text-sm text-white/60">{product.category}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{product.barcode}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{product.price}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">{product.stock}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-3">
                          <button
                            onClick={() => handleViewProduct(product.id)}
                            className="text-blue-400 hover:text-blue-300 transition-colors"
                            title="Voir"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleEditProduct(product.id)}
                            className="text-amber-400 hover:text-amber-300 transition-colors"
                            title="Modifier"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : searchQuery && !isLoading ? (
            <div className="bg-white/5 rounded-2xl p-8 border border-white/10 flex flex-col items-center">
              <QrCodeIcon className="h-12 w-12 text-white/30 mb-2" />
              <h3 className="text-lg font-medium text-white mb-1">Aucun produit trouvé</h3>
              <p className="text-white/60 text-center mb-4">Aucun produit correspondant au code-barres "{searchQuery}" n'a été trouvé.</p>
              <button
                onClick={() => handleCreateProductWithBarcode(searchQuery)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center space-x-2"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Créer un produit avec ce code-barres</span>
              </button>
            </div>
          ) : null}
        </div>

        {/* Produits récemment scannés */}
        {recentlyScanned.length > 0 && (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <h2 className="text-xl font-semibold text-white mb-4">Récemment scannés</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentlyScanned.map((product) => (
                <div 
                  key={product.id}
                  className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-white/30 transition-all hover:bg-white/10 cursor-pointer"
                  onClick={() => handleViewProduct(product.id)}
                >
                  <div className="flex items-center space-x-3">
                    {product.thumbnail && (
                      <div className="h-12 w-12 rounded overflow-hidden">
                        <img src={product.thumbnail} alt={product.name} className="h-full w-full object-cover" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{product.name}</p>
                      <p className="text-xs text-white/60 mt-1">{product.barcode}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-white">{product.price}</p>
                      <p className="text-xs text-white/60 mt-1">Stock: {product.stock}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Scanner de code-barres modal */}
      <BarcodeScanner 
        isOpen={isScannerOpen}
        onClose={closeScanner}
        onScan={handleScan}
        onError={handleScanError}
        mode="search"
      />
    </>
  );
};

// Composant principal pour le routage
const ProductsPage = () => {
  return (
    <Routes>
      <Route index element={<ProductsMainPage />} />
      <Route path="categories" element={<ProductsMainPage />} />
      <Route path="barcode/*" element={<ProductsMainPage />} />
      <Route path="new" element={<ProductForm />} />
      <Route path=":id" element={<ProductDetail />} />
      <Route path=":id/edit" element={<ProductForm />} />
    </Routes>
  );
};

export default ProductsPage; 